/* eslint-disable no-unused-vars */
import {useCallback, useRef, useEffect} from "react";
import {ButtonAddTable} from "components/Button";
import {DashboardLayout} from "components/Layout";
import {SearchBox} from "components/Table";
import {useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {DoctorPriceTable} from "components/DoctorPriceTable";
import {ModalContainer} from "components/Modal";
import {Formik} from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import {useAddPrice, useEditPrice} from "hooks/Doctor/Price";
import {useToast} from "hooks/useToast";
import {convertErrorMessageFormat, convertToCurency} from "utils/converter";
import {Select} from "components/Form";

const FormSchemaValidation = Yup.object().shape({
	price: Yup.string().required("Harga Baru wajib di isi"),
	hospitalId: Yup.mixed().required("Rumah sakit wajib di isi"),
});
const FormInitialValues = {
	price: "",
	hospitalId: "",
};

export function PriceListPage() {
	const userProfile = useSelector(state => state.AuthReducer.profile);
	const [searchKeyword, setSearchKeyword] = useState("");
	const {showToast} = useToast();

	// simpan status data tiap RS
	const [dataStatus, setDataStatus] = useState({}); // { hospitalId: true/false }

	const hospitals = useMemo(() => {
		if (userProfile?.id) {
			return userProfile?.staff?.rsMany?.filter(item => item.roleType == 2 && item.Hospital?.name) || [];
		}
	}, [userProfile]);

	// callback dari child
	const handleDataStatusChange = (hospitalId, hasData) => {
		setDataStatus(prev => ({
			...prev,
			[hospitalId]: hasData,
		}));
	};

	const allHospitalsNoData = hospitals?.length > 0 && hospitals.every(item => dataStatus[item.id] === false);

	const [isOpenAdd, setIsOpenAdd] = useState(false);
	const {mutate: mutateAdd, isLoading: isLoadingAdd} = useAddPrice();
	const formikRef = useRef();
	const handleAdd = useCallback(() => {
		const formData = {
			price: formikRef.current.values.price,
			hospitalId: formikRef.current.values.hospitalId.value || formikRef.current.values.hospitalId,
		};

		mutateAdd(formData, {
			onSuccess: () => {
				showToast("success", "Data berhasil ditambahkan", 3000);
				setIsOpenAdd(false);
			},
			onError: res => {
				showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
			},
		});
	}, [mutateAdd, showToast]);
	const [isOpenEdit, setIsOpenEdit] = useState(false);
	const [editId, setEditId] = useState(null);
	const {mutate: mutateEdit, isLoading: isLoadingEdit} = useEditPrice();
	const formikRefEdit = useRef();
	const handleEdit = useCallback(() => {
		const formData = {
			id: editId,
			price: formikRefEdit.current.values.price,
		};

		mutateEdit(formData, {
			onSuccess: () => {
				showToast("success", "Data berhasil diubah", 3000);
				setIsOpenEdit(false);
			},
			onError: res => {
				showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
			},
		});
	}, [editId, mutateEdit, showToast]);

	return (
		<DashboardLayout>
			<span className="text-bold text-dark mb-4">Harga</span>
			<SearchBox
				rightElement={<ButtonAddTable text="Request Harga Baru" onClick={() => setIsOpenAdd(true)} />}
				onSearch={keyword => {
					setSearchKeyword(keyword);
				}}
			/>

			{/* pesan global jika semua RS kosong */}
			{allHospitalsNoData && (
				<div className="p-4 text-center text-muted">Semua rumah sakit belum memiliki data harga.</div>
			)}

			{hospitals?.map(item => (
				<DoctorPriceTable
					key={item.id}
					hospitalId={item?.Hospital?.id}
					hospitalName={item?.Hospital?.name}
					search={searchKeyword}
					onDataStatusChange={handleDataStatusChange}
					onEditRow={row => {
						setEditId(row.id);
						formikRefEdit.current.setFieldValue("oldPrice", row.consultationFee?.amount || "");
						formikRefEdit.current.setFieldValue("price", "");
						formikRefEdit.current.setFieldValue("hospitalId", {
							label: item.Hospital?.name,
							value: item.id,
						});
						setIsOpenEdit(true);
					}}
				/>
			))}
			<ModalContainer visible={isOpenAdd} onClose={() => setIsOpenAdd(false)}>
				<div className="box-popup w-25" id="change-price">
					<div className="d-flex justify-content-between align-items-center mb-4">
						<div className="text-bold text-dark">Request Harga Baru</div>
						<div className="for-close">
							<button className="btn btn-close" type="button" onClick={() => setIsOpenAdd(false)} />
						</div>
					</div>

					<div className="popup-content">
						<Formik
							initialValues={FormInitialValues}
							innerRef={formikRef}
							validationSchema={FormSchemaValidation}
							onSubmit={handleAdd}>
							{({
								handleSubmit,
								handleBlur,
								handleChange,
								values,
								errors,
								touched,
								setFieldValue,
								setFieldTouched,
								resetForm,
							}) => (
								<>
									<div class="row gx-2 mb-5">
										<div class="col-12">
											<div>
												<Select
													required
													label="Rumah Sakit"
													name="hospitalId"
													options={[
														{label: "Pilih rumah sakit...", value: "", disabled: true},
														...(hospitals?.map(h => ({
															label: h.Hospital?.name,
															value: h.Hospital?.id,
														})) || []),
													]}
													placeholder="Pilih rumah sakit..."
													onChange={selected => {
														setFieldValue("hospitalId", selected);
													}}
												/>
												{errors.hospitalId && touched.hospitalId && (
													<p className="form-error-item-message">{errors.hospitalId}</p>
												)}
											</div>
											<div class="setting-item">
												<label class="setting-label required">Request Harga</label>
												<input
													class={clsx(
														"form-control",
														errors?.quota && touched?.price && "form-control-error",
													)}
													disabled={isLoadingAdd}
													name="price"
													placeholder=""
													type="text"
													value={values?.price ? convertToCurency(values?.price) : null}
													onBlur={handleBlur}
													onChange={e => {
														const value = e.target.value.replace(/\D/g, "");

														setFieldValue("price", value);
													}}
												/>
												{errors.price && touched.price && (
													<p className="form-error-item-message">{errors.price}</p>
												)}
											</div>
										</div>
									</div>
									<div class="col-12 text-center">
										<button
											className="btn btn-orange-border r8 mx-2"
											type="button"
											onClick={() => {
												resetForm();
												setIsOpenAdd(false);
											}}>
											Kembali
										</button>
										<button
											className="btn btn-orange r8 mx-2"
											disabled={!FormSchemaValidation.isValidSync(values)}
											type="button"
											onClick={handleSubmit}>
											Ajukan
										</button>
									</div>
								</>
							)}
						</Formik>
					</div>
				</div>
			</ModalContainer>
			<ModalContainer visible={isOpenEdit} onClose={() => setIsOpenEdit(false)}>
				<div className="box-popup w-25" id="change-price">
					<div className="d-flex justify-content-between align-items-center mb-4">
						<div className="text-bold text-dark">Request Harga Baru</div>
						<div className="for-close">
							<button className="btn btn-close" type="button" onClick={() => setIsOpenEdit(false)} />
						</div>
					</div>

					<div className="popup-content">
						<Formik
							initialValues={FormInitialValues}
							innerRef={formikRefEdit}
							validationSchema={FormSchemaValidation}
							onSubmit={handleEdit}>
							{({
								handleSubmit,
								handleBlur,
								handleChange,
								values,
								errors,
								touched,
								setFieldValue,
								setFieldTouched,
								resetForm,
							}) => (
								<>
									<div class="row gx-2 mb-5">
										<div class="col-12">
											<div>
												<Select
													required
													label="Rumah Sakit"
													name="hospitalId"
													options={[
														{label: "Pilih rumah sakit...", value: "", disabled: true},
														...(hospitals?.map(h => ({
															label: h.Hospital?.name,
															value: h.Hospital?.id,
														})) || []),
													]}
													placeholder="Pilih rumah sakit..."
													onChange={selected => {
														setFieldValue("hospitalId", selected);
													}}
												/>
												{errors.hospitalId && touched.hospitalId && (
													<p className="form-error-item-message">{errors.hospitalId}</p>
												)}
											</div>
											<div class="setting-item">
												<label class="setting-label required">Harga Lama</label>
												<input
													disabled
													class={clsx("form-control")}
													name="oldPrice"
													placeholder=""
													type="text"
													value={values?.oldPrice}
												/>
												{errors.price && touched.price && (
													<p className="form-error-item-message">{errors.price}</p>
												)}
											</div>
											<div class="setting-item">
												<label class="setting-label required">Request Harga Baru</label>
												<input
													class={clsx(
														"form-control",
														errors?.quota && touched?.price && "form-control-error",
													)}
													disabled={isLoadingEdit}
													name="price"
													placeholder=""
													type="text"
													value={values?.price ? convertToCurency(values?.price) : null}
													onBlur={handleBlur}
													onChange={e => {
														const value = e.target.value.replace(/\D/g, "");

														setFieldValue("price", value);
													}}
												/>
												{errors.price && touched.price && (
													<p className="form-error-item-message">{errors.price}</p>
												)}
											</div>
										</div>
									</div>
									<div class="col-12 text-center">
										<button
											className="btn btn-orange-border r8 mx-2"
											type="button"
											onClick={() => {
												resetForm();
												setIsOpenAdd(false);
											}}>
											Kembali
										</button>
										<button
											className="btn btn-orange r8"
											disabled={!FormSchemaValidation.isValidSync(values)}
											type="button"
											onClick={handleSubmit}>
											Ajukan
										</button>
									</div>
								</>
							)}
						</Formik>
					</div>
				</div>
			</ModalContainer>
		</DashboardLayout>
	);
}
